import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import clearImpersonationLocalStorage from '../../global/clearImpersonationLocalStorage';
import { broadcastImpersonationStarted, initImpersonationChannel } from '../../global/impersonationChannel';

const ImpersonationSetup = ({ match: { params: { id } } }) => {
  const [status, setStatus] = useState('loading_user');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    initImpersonationChannel();
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`/super_admin/m8_users/${id}/impersonation_info`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          const body = await response.json().catch(() => null);
          const errorMessage = body?.error || `Failed to load user info: ${response.status}`;
          setError(errorMessage);
          setStatus('error');
          return;
        }

        const info = await response.json();

        if (info.revoked) {
          setError('Cannot impersonate a revoked user.');
          setStatus('error');
          return;
        }

        setUserInfo(info);
        setStatus('ready');
      } catch (e) {
        setError(`Failed to load user info: ${e.message}`);
        setStatus('error');
      }
    };

    fetchUserInfo();
  }, [id]);

  const start = useCallback(async () => {
    if (loading) return; // Prevent double-clicks

    const csrfToken = document.getElementsByName('csrf-token')[0]?.content;

    if (!csrfToken) {
      setError('Missing CSRF token.');
      setStatus('error');
      return;
    }

    try {
      setStatus('Starting impersonation session...');
      setLoading(true);
      setError(null);

      const response = await fetch(`/super_admin/m8_users/${id}/start_impersonation`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });

      const contentType = response.headers.get('content-type') || '';
      let body = null;
      if (contentType.includes('application/json')) {
        try {
          body = await response.json();
        } catch {
          body = null;
        }
      }

      if (!response.ok || !contentType.includes('application/json') || body?.ok !== true) {
        let errorMessage = (body && body.error) ? body.error : null;
        if (!errorMessage) {
          if (response.status === 401) errorMessage = 'Your super admin session may have expired. Please sign in again.';
          else if (response.status === 403) errorMessage = 'You do not have permission to impersonate users.';
          else if (response.ok) errorMessage = 'Unexpected response from server. Please refresh and try again.';
          else errorMessage = `Failed to start impersonation: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      setStatus('Clearing cached session data...');
      try {
        clearImpersonationLocalStorage();
        // Re-set impersonation flag so validateAuth() on the next page load
        // knows we're impersonating before getLoginInfo() fetches server state.
        // Without this, m8_impersonating is cleared and the banner never shows.
        localStorage.setItem('m8_impersonating', 'true');
      } catch {
        // If localStorage is unavailable, proceed; the server-side session still impersonates correctly.
      }

      // Notify other tabs that impersonation has started
      broadcastImpersonationStarted({ userId: id });

      setStatus('Success! Redirecting...');
      // Full page reload ensures clean Redux state (no stale data from previous user)
      window.location.href = '/app';
    } catch (e) {
      setError(e.message);
      setStatus('error');
      setLoading(false);
    }
  }, [id, loading]);

  const getButtonText = () => {
    if (loading) return 'Starting...';
    if (error) return 'Retry';
    return 'Start Impersonation';
  };

  const isReady = status === 'ready';
  const isLoadingUser = status === 'loading_user';

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      margin: 0,
      background: '#f5f5f5',
      padding: '20px',
    }}
    >
      <div style={{
        textAlign: 'center',
        padding: '40px',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        maxWidth: '520px',
        width: '100%',
      }}
      >
        <h2 style={{ color: '#333', marginBottom: '10px' }}>
          {isReady ? 'Start Impersonation' : 'Setting Up Impersonation'}
        </h2>
        {(() => {
          try {
            return localStorage.getItem('m8_impersonating') === 'true';
          } catch {
            return false;
          }
        })() && (
          <p style={{ color: '#b25b00', fontWeight: 'bold', margin: '10px 0' }}>
            Please close any other impersonation tabs first!
          </p>
        )}
        {isLoadingUser && (
          <p style={{ color: '#666', margin: '10px 0' }}>
            Loading user info...
          </p>
        )}
        {isReady && userInfo && (
          <div style={{
            margin: '20px 0', textAlign: 'left', background: '#f8f9fa', padding: '15px', borderRadius: '4px',
          }}
          >
            <p style={{ color: '#333', margin: '0 0 8px 0', fontWeight: 'bold' }}>
              You are about to impersonate:
            </p>
            <p style={{ color: '#666', margin: '4px 0' }}>
              <strong>Email:</strong>
              {' '}
              {userInfo.email}
            </p>
            <p style={{ color: '#666', margin: '4px 0' }}>
              <strong>Organization:</strong>
              {' '}
              {userInfo.organization_name || 'N/A'}
            </p>
            <p style={{ color: '#767676', margin: '4px 0', fontSize: '12px' }}>
              User ID:
              {' '}
              {id}
            </p>
          </div>
        )}
        <div aria-live="polite" aria-atomic="true">
          {!isReady && !isLoadingUser && !error && (
            <p style={{ color: '#666', margin: '10px 0' }}>
              {status}
            </p>
          )}
        </div>
        {error && (
          <p role="alert" style={{ color: '#c0392b', margin: '10px 0' }}>
            Error:
            {' '}
            {error}
          </p>
        )}
        {(isReady || error) && (
          <button
            type="button"
            onClick={start}
            disabled={loading || isLoadingUser}
            aria-busy={loading}
            aria-label={userInfo ? `Start impersonation session for ${userInfo.email}` : `Start impersonation session for user ${id}`}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              cursor: (loading || isLoadingUser) ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              border: 'none',
              background: (loading || isLoadingUser) ? '#636363' : '#2574a9',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px',
              opacity: (loading || isLoadingUser) ? 0.7 : 1,
            }}
          >
            {getButtonText()}
          </button>
        )}
        {(isReady || error) && (
          <a
            href="/super_admin/m8_users"
            style={{
              display: 'block',
              marginTop: '15px',
              color: '#666',
              textDecoration: 'underline',
              fontSize: '13px',
            }}
          >
            Cancel and return to Users
          </a>
        )}
      </div>
    </div>
  );
};

ImpersonationSetup.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default ImpersonationSetup;
